import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, ChevronRight } from "lucide-react";
import { listTurnovers, listUnits, listProperties, listTenants } from "@/lib/api";
import { germanDate } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";

const phaseShort = { 1: "Kündigung", 2: "Vorabnahme", 3: "Übergabe", 4: "Abschluss" };

const Turnovers = () => {
    const [turnovers, setTurnovers] = useState([]);
    const [units, setUnits] = useState([]);
    const [properties, setProperties] = useState([]);
    const [tenants, setTenants] = useState([]);

    useEffect(() => {
        (async () => {
            const [tv, u, p, te] = await Promise.all([
                listTurnovers(), listUnits(), listProperties(), listTenants(),
            ]);
            setTurnovers(tv); setUnits(u); setProperties(p); setTenants(te);
        })();
    }, []);

    return (
        <div className="px-6 md:px-10 py-8 space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Prozesse</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="turnovers-title">Mieterwechsel</h1>
                    <p className="text-sm text-gray-500 mt-1">Alle aktiven und archivierten Übergabeprozesse.</p>
                </div>
                <Link to="/wechsel/neu">
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-turnover-list">
                        <Plus size={14} className="mr-1.5" /> Neu starten
                    </Button>
                </Link>
            </div>

            {/* overflow-x-auto, nicht overflow-hidden: die Tabelle ist breiter als der
                Bildschirm, sobald genug Spalten da sind — mit "hidden" verschwinden die
                rechten Spalten lautlos und sind auf keinem Weg erreichbar. Gemessen am
                10.09.2026 bei 1280 px: Mieter 210 px abgeschnitten, Einheiten 183 px. */}
            <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.12em] text-gray-500">
                        <tr>
                            <th className="text-left font-medium px-4 py-3">Einheit</th>
                            <th className="text-left font-medium px-4 py-3">Immobilie</th>
                            <th className="text-left font-medium px-4 py-3">Alt-Mieter</th>
                            <th className="text-left font-medium px-4 py-3">Neu-Mieter</th>
                            <th className="text-left font-medium px-4 py-3">Phase</th>
                            <th className="text-left font-medium px-4 py-3">Status</th>
                            <th className="text-left font-medium px-4 py-3">Aktualisiert</th>
                            <th className="text-right font-medium px-4 py-3 sticky right-0 bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {turnovers.map((t) => {
                            const u = units.find((x) => x.id === t.unit_id);
                            const p = u ? properties.find((x) => x.id === u.property_id) : null;
                            const oldT = tenants.find((x) => x.id === t.old_tenant_id);
                            const newT = tenants.find((x) => x.id === t.new_tenant_id);
                            return (
                                <tr key={t.id} className="group hover:bg-gray-50" data-testid={`turnover-row-${t.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-900">{u?.unit_label || "—"}</div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">
                                        <div className="text-xs">{p?.label || p?.address || "—"}</div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">{oldT?.full_name || "—"}</td>
                                    <td className="px-4 py-3 text-gray-700">{newT?.full_name || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-2 text-xs font-mono tabular-nums">
                                            <span className="w-5 h-5 grid place-items-center rounded-full bg-slate-900 text-white text-[10px]">{t.current_phase}</span>
                                            {phaseShort[t.current_phase]}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                                    <td className="px-4 py-3 text-gray-500 tabular-nums">{germanDate(t.updated_at)}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                                        <Link to={`/wechsel/${t.id}`} data-testid={`view-turnover-${t.id}`}>
                                            <Button variant="ghost" size="sm" className="h-8">Öffnen <ChevronRight size={14} className="ml-1" /></Button>
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                        {turnovers.length === 0 && (
                            <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Noch keine Mieterwechsel.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Turnovers;
